var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Checkbox, Text } from '@ui';
import './QboTaxPrecheckModalViewStyles.scss';
/**
 * A guard on the invoice "Send to QuickBooks" action.
 *
 * A React island in `invoices/show`. It wires the existing server-rendered
 * `#qbo-send-link` control: on click it POSTs to the tax pre-check
 * (`invoices#qbo_tax_precheck`) BEFORE the send. If the invoice's tax setup
 * lines up with the QuickBooks company's, or the user has dismissed the
 * warning, it fires the normal `send_to_qbo` POST unchanged. Otherwise it
 * opens an @ui Modal explaining the mismatch.
 *
 * The modal never hard-blocks: "Continue Sync" always proceeds, and the guard
 * fails open on any pre-check error. A secondary self-service link is shown
 * only when the server supplies one.
 *
 * The send is a real form submit rather than an XHR: `send_to_qbo` answers with
 * a redirect + flash that only a browser navigation can follow (same reasoning
 * as DeactivateTechnician).
 */
function csrfToken() {
    var _a, _b;
    return (_b = (_a = document.querySelector('meta[name="csrf-token"]')) === null || _a === void 0 ? void 0 : _a.getAttribute('content')) !== null && _b !== void 0 ? _b : '';
}
function jsonHeaders() {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() };
}
const DISMISS_CHECKBOX_ID = 'qbo-tax-precheck-dismiss';
export function QboTaxPrecheckModalView({ triggerId, precheckUrl, sendUrl, dismissUrl, labels }) {
    var _a;
    const [verdict, setVerdict] = useState(null);
    const [dismissChecked, setDismissChecked] = useState(false);
    const [continuing, setContinuing] = useState(false);
    const busyRef = useRef(false);
    const submitSend = useCallback(() => {
        const form = document.createElement('form');
        form.method = 'post';
        form.action = sendUrl;
        form.style.display = 'none';
        const token = document.createElement('input');
        token.type = 'hidden';
        token.name = 'authenticity_token';
        token.value = csrfToken();
        form.appendChild(token);
        document.body.appendChild(form);
        form.submit();
    }, [sendUrl]);
    const runPrecheck = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (busyRef.current)
            return;
        busyRef.current = true;
        try {
            const res = yield fetch(precheckUrl, {
                method: 'POST',
                headers: jsonHeaders(),
                body: '{}'
            });
            if (!res.ok) {
                // Fail open: an HTTP error can still carry a parseable JSON body (an ordinary
                // Rails `{ error: ... }`). Never parse an error response as a verdict.
                submitSend();
                return;
            }
            const data = (yield res.json());
            if (!data || data.will_transmit || data.suppressed || !data.title) {
                submitSend();
            }
            else {
                setVerdict(data);
                busyRef.current = false;
            }
        }
        catch (_a) {
            submitSend();
        }
    }), [precheckUrl, submitSend]);
    useEffect(() => {
        const trigger = document.getElementById(triggerId);
        if (!trigger)
            return undefined;
        const onClick = (event) => {
            event.preventDefault();
            void runPrecheck();
        };
        trigger.addEventListener('click', onClick);
        return () => trigger.removeEventListener('click', onClick);
    }, [triggerId, runPrecheck]);
    const close = useCallback(() => {
        setVerdict(null);
        setContinuing(false);
    }, []);
    const rememberDismissal = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        try {
            yield fetch(dismissUrl, {
                method: 'PATCH',
                headers: jsonHeaders(),
                body: '{}'
            });
        }
        catch (_a) {
            // The send must not depend on the preference being saved.
        }
    }), [dismissUrl]);
    const continueSync = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (busyRef.current)
            return;
        busyRef.current = true;
        setContinuing(true);
        if (dismissChecked)
            yield rememberDismissal();
        close();
        submitSend();
    }), [dismissChecked, rememberDismissal, close, submitSend]);
    const openCta = useCallback(() => {
        var _a;
        const href = (_a = verdict === null || verdict === void 0 ? void 0 : verdict.cta) === null || _a === void 0 ? void 0 : _a.href;
        if (!href)
            return;
        window.open(href, '_blank', 'noopener');
    }, [verdict]);
    // Render the modal only while there's a verdict — the @ui Modal validates
    // that ariaLabel is non-empty on every render, so mounting it closed with an
    // empty label would throw.
    if (verdict === null)
        return null;
    return (React.createElement(Modal, { isOpen: true, onClose: close, variant: "base", triggerElementId: triggerId, ariaLabel: verdict.title },
        React.createElement(ModalHeader, { title: verdict.title, onClose: close }),
        React.createElement(ModalBody, null,
            React.createElement("div", { className: "qbo-tax-precheck__body" },
                React.createElement(Text, { variant: "body", as: "p" }, verdict.message),
                React.createElement(Checkbox, { id: DISMISS_CHECKBOX_ID, checked: dismissChecked, onChange: setDismissChecked, label: labels.dontShowAgain }))),
        React.createElement(ModalFooter, null,
            React.createElement(Button, { type: "button", variant: "default", onClick: close }, labels.cancel),
            ((_a = verdict.cta) === null || _a === void 0 ? void 0 : _a.href) && verdict.cta.label && (React.createElement(Button, { type: "button", variant: "secondary", onClick: openCta }, verdict.cta.label)),
            React.createElement(Button, { type: "button", variant: "primary", disabled: continuing, onClick: () => void continueSync() }, labels.continueSync))));
}
